import { debounceTime, map } from 'rxjs/operators';
import { NgForOf, NgSwitch, NgSwitchCase } from '@angular/common';
import { distinctUntilChanged, filter, interval, merge, Observable, Subscription } from 'rxjs';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';

import { UserRoleEnum } from '@enums/user-role.enum';
import { EventConstant } from '../../../../sse/event.constant';
import { SignalrService } from '../../../../sse/signalr.service';
import { LocalStorageService } from '@services/local-storage.service';
import { ILoggedInUserViewModel } from '../../../../model/common.model';
import { SignalRMessage } from '@interfaces/signal-r-message.interface';
import { NotificationType } from '@modules/notification-queue/types/notification.type';
import { registerStackedListItemAnimation } from '../../../animations/stacked-list-item.animation';
import { InternalTripNotificationComponent } from '@modules/notification-queue/modules/internal-trip-notification/component/internal-trip-notification.component';
import { ServiceRequestCreatedSignalRInterface } from '@interfaces/singnal-r/service-request-created-signal-r.interface';
import { ServiceRequestNotificationComponent } from '@modules/notification-queue/modules/service-request-notification/component/service-request-notification.component';
import { InternalTripNotificationSignalRInterface } from '@interfaces/singnal-r/internal-trip-notification-signal-r.interface';
import { ServiceRequestFlagNotificationComponent } from '../modules/service-request-flag-notification/service-request-flag-notification.component';
import { ServiceRequestFlagsSignalRInterface } from '@interfaces/singnal-r/service-request-flags-signal-r.interface';

@Component({
  selector: 'app-notification-queue',
  standalone: true,
  templateUrl: './notification-queue.component.html',
  styleUrls: ['./notification-queue.component.scss'],
  imports: [
    NgForOf,
    NgSwitch,
    NgSwitchCase,
    ServiceRequestNotificationComponent,
    InternalTripNotificationComponent,
    ServiceRequestFlagNotificationComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [registerStackedListItemAnimation()],
})
export class NotificationQueueComponent implements OnInit, OnDestroy {
  notificationQueue: SignalRMessage<NotificationType | ServiceRequestFlagsSignalRInterface>[] = [];

  readonly eventList = EventConstant;

  private readonly currentUser: ILoggedInUserViewModel = this.localStorageService.getCurrentUserDetails();

  private readonly subscriptions: Subscription = new Subscription();

  private serviceRequestCreatedEvent$: Observable<SignalRMessage<ServiceRequestCreatedSignalRInterface>> =
    this.signalRService.eventDataChange.pipe(
      filter(
        (event: SignalRMessage<ServiceRequestCreatedSignalRInterface>): boolean =>
          event.eventName === EventConstant.serviceRequestCreatedEvent && event.data.isCustomerSrInQueue,
      ),
      debounceTime(100),
      distinctUntilChanged(
        (
          a: SignalRMessage<ServiceRequestCreatedSignalRInterface, { ServiceRequestId: string }>,
          b: SignalRMessage<ServiceRequestCreatedSignalRInterface, { ServiceRequestId: string }>,
        ): boolean => a.extraParams.ServiceRequestId === b.extraParams.ServiceRequestId,
      ),
    );

  private internalTripNotificationEvent$: Observable<SignalRMessage<InternalTripNotificationSignalRInterface>> =
    this.signalRService.eventDataChange.pipe(
      filter(
        (event: SignalRMessage<InternalTripNotificationSignalRInterface>): boolean =>
          event.eventName === EventConstant.internalTripNotificationEvent,
      ),
      filter(
        ({ data }: SignalRMessage<InternalTripNotificationSignalRInterface>): boolean =>
          (this.currentUser.role === UserRoleEnum.COORDINATOR && data.assignedCoordinator === this.currentUser.memberId) ||
          this.currentUser.role === UserRoleEnum.MANAGEMENT,
      ),
    );
  private serviceRequestFlagEvent$ = this.signalRService.eventDataChange.pipe(
    filter(
      (event: SignalRMessage<ServiceRequestFlagsSignalRInterface>): boolean => event.eventName === EventConstant.serviceRequestFlagEvent,
    ),
    map((signalRMessage: SignalRMessage<ServiceRequestFlagsSignalRInterface>): SignalRMessage<ServiceRequestFlagsSignalRInterface> => {
      return {
        ...signalRMessage,
        data: {
          ...signalRMessage.data,
          notificationId: signalRMessage.data.ServiceRequestId + new Date(signalRMessage.data.FollowUpTime).getTime().toString(),
          isFlagNotification: true,
        },
      };
    }),
  );

  constructor(
    private readonly signalRService: SignalrService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly localStorageService: LocalStorageService,
  ) {}

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  ngOnInit() {
    this.registerEventListeners();
    this.subscribeOnNotifications();
  }

  /**
   * TrackBy function to track notification by it's id
   * @param _index - notification index
   * @param item - notification object
   */
  trackByNotificationId(_index: number, item: SignalRMessage<NotificationType>): string {
    return item.data.notificationId;
  }

  /**
   * Remove notification from the notification queue and mark component for change detector check
   * @param notificationId - the notification's id to close
   */
  onCloseNotification(notificationId: string): void {
    this.notificationQueue = this.notificationQueue.filter(
      (notification: SignalRMessage<NotificationType>) => notification.data.notificationId !== notificationId,
    );

    this.changeDetectorRef.markForCheck();
  }

  /**
   * Register signal R event listeners
   */
  private registerEventListeners(): void {
    this.signalRService.sseEventListener(EventConstant.serviceRequestCreatedEvent);
    this.signalRService.sseEventListener(EventConstant.internalTripNotificationEvent);
    this.signalRService.sseEventListener(EventConstant.serviceRequestFlagEvent);
  }

  /**
   * Subscribe on signal R events. Saves them in a notification queue array and marks component for change detector check
   * once any observable value is emitted
   */
  private subscribeOnNotifications(): void {
    const subscription = merge(this.serviceRequestCreatedEvent$, this.internalTripNotificationEvent$).subscribe(
      (event: SignalRMessage<NotificationType>) => {
        this.notificationQueue.push(event);
        this.changeDetectorRef.markForCheck();
      },
    );
    const subscription2 = this.serviceRequestFlagEvent$.subscribe((event: SignalRMessage<ServiceRequestFlagsSignalRInterface>) => {
      this.notificationQueue.push(event);
      this.changeDetectorRef.markForCheck();
    });

    this.subscriptions.add(subscription);
    this.subscriptions.add(subscription2);
  }
}
